import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Card,
  CardContent,
  Grid,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { documentTypesAPI, clientDocumentsAPI, clientsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ClientDocumentsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_staff || user?.is_superuser;
  
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    client: null,
    document_type: '',
    name: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docsRes, typesRes] = await Promise.all([
        isAdmin ? clientDocumentsAPI.getAll() : clientDocumentsAPI.getMyDocuments(),
        documentTypesAPI.getActive(),
      ]);
      
      setDocuments(docsRes.data.results || docsRes.data || []);
      setDocumentTypes(typesRes.data.results || typesRes.data || []);
      
      if (isAdmin) {
        const clientsRes = await clientsAPI.getAll();
        setClients(clientsRes.data || []);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        client: clients.find(c => c.id === doc.client) || null,
        document_type: doc.document_type,
        name: doc.name,
        notes: doc.notes || '',
      });
      setSelectedFile(null);
    } else {
      setEditingDoc(null);
      setFormData({
        client: null,
        document_type: '',
        name: '',
        notes: '',
      });
      setSelectedFile(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDoc(null);
    setSelectedFile(null);
    setFormData({
      client: null,
      document_type: '',
      name: '',
      notes: '',
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setError('Seuls les fichiers PDF sont autorisés');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas dépasser 10 Mo');
        return;
      }
      setSelectedFile(file);
      if (!formData.name) {
        setFormData({ ...formData, name: file.name.replace('.pdf', '') });
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.document_type || !formData.name.trim()) {
      setError('Le type de document et le nom sont obligatoires');
      return;
    }
    
    if (!editingDoc && !selectedFile) {
      setError('Veuillez sélectionner un fichier PDF');
      return;
    }

    try {
      setSubmitting(true);
      const submitData = new FormData();
      
      if (isAdmin && formData.client) {
        submitData.append('client', formData.client.id);
      }
      submitData.append('document_type', formData.document_type);
      submitData.append('name', formData.name);
      submitData.append('notes', formData.notes);
      
      if (selectedFile) {
        submitData.append('file', selectedFile);
      }

      if (editingDoc) {
        await clientDocumentsAPI.update(editingDoc.id, submitData);
        setSuccess('Document mis à jour avec succès');
      } else {
        await clientDocumentsAPI.create(submitData);
        setSuccess('Document ajouté avec succès');
      }
      
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Error saving document:', err);
      setError(err.response?.data?.message || err.response?.data?.file?.[0] || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await clientDocumentsAPI.delete(id);
      setSuccess('Document supprimé avec succès');
      loadData();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Erreur lors de la suppression');
    }
  };

  const handleDownload = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {isAdmin ? 'Documents Clients' : 'Mes Documents'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAdmin ? 'Gérer les documents PDF des clients' : 'Gérer vos documents PDF'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Document
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Documents
              </Typography>
              <Typography variant="h4" sx={{ mt: 1 }}>
                {documents.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {isAdmin && <TableCell><strong>Client</strong></TableCell>}
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    Aucun document trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  {isAdmin && (
                    <TableCell>
                      <Typography variant="body2">
                        {doc.client_name || doc.client_code || '-'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PdfIcon color="error" fontSize="small" />
                      <Typography variant="body1" fontWeight={500}>
                        {doc.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.document_type_name}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Télécharger">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleDownload(doc.file_url)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(doc)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(doc.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDoc ? 'Modifier le Document' : 'Nouveau Document'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {isAdmin && (
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => `${option.company_name} (${option.client_code})`}
                value={formData.client}
                onChange={(e, newValue) => setFormData({ ...formData, client: newValue })}
                renderInput={(params) => (
                  <TextField {...params} label="Client *" required />
                )}
              />
            )}
            
            <FormControl fullWidth required>
              <InputLabel>Type de Document *</InputLabel>
              <Select
                value={formData.document_type}
                label="Type de Document *"
                onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Nom du Document *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Notes optionnelles"
            />
            
            <Box>
              <input
                accept="application/pdf"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  {selectedFile ? selectedFile.name : (editingDoc ? 'Changer le fichier (optionnel)' : 'Sélectionner un fichier PDF *')}
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Taille: {(selectedFile.size / 1024 / 1024).toFixed(2)} Mo
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Enregistrement...' : (editingDoc ? 'Modifier' : 'Ajouter')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientDocumentsPage;
